import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Login from './views/Login';
import JiraData from './views/JiraData';

const REQUIRED_ROLE = 'Athena User';

function decodeJwtPayload(token: string | undefined): Record<string, unknown> {
  if (!token) return {};
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return {};
  }
}

function hasRole(claims: Record<string, unknown>, role: string): boolean {
  const val = claims['role'] ?? claims['roles'];
  if (Array.isArray(val)) return val.includes(role);
  if (typeof val === 'string') return val === role;
  return false;
}

function ErrorComponent({ message }: { message?: string }): React.ReactElement {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center max-w-md px-8">
        <h1 className="text-2xl font-bold text-red-600 mb-3">Aanmelding mislukt</h1>
        <p className="text-neutral-600 mb-4">
          {message || 'Er is een onbekende fout opgetreden.'}
        </p>
      </div>
    </div>
  );
}

function LoadingComponent(): React.ReactElement {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <p className="text-neutral-500 text-lg">Bezig met aanmelden...</p>
    </div>
  );
}

function NoAccessComponent(): React.ReactElement {
  const auth = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center max-w-md px-8">
        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
          <i className="fa-light fa-lock text-2xl text-amber-500" />
        </div>
        <h1 className="text-2xl font-bold text-neutral-900 mb-3">Geen toegang</h1>
        <p className="text-neutral-600 mb-6">
          Je account heeft geen toegang tot Athena.
        </p>
        <button
          onClick={() => auth.signoutRedirect()}
          className="px-4 py-2 text-sm text-neutral-600 border border-neutral-300 rounded-lg hover:bg-neutral-100 transition-colors"
        >
          Afmelden
        </button>
      </div>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactElement }): React.ReactElement {
  const auth = useAuth();

  if (auth.isLoading) return <LoadingComponent />;
  if (auth.error) return <ErrorComponent message={auth.error.message} />;
  if (!auth.isAuthenticated) {
    auth.signinRedirect();
    return <LoadingComponent />;
  }
  const accessTokenClaims = decodeJwtPayload(auth.user?.access_token);
  if (!hasRole(accessTokenClaims, REQUIRED_ROLE)) {
    return <NoAccessComponent />;
  }
  return children;
}

function AuthRoot(): React.ReactElement {
  const auth = useAuth();
  if (auth.isLoading) return <LoadingComponent />;
  if (auth.isAuthenticated) return <Navigate to="/home" replace />;
  return <Login />;
}

export default function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<AuthRoot />} />
        <Route
          path="/home"
          element={
            <RequireAuth>
              <JiraData />
            </RequireAuth>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
